import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    useFragment, useReactiveVar, useMutation, useQuery, useApolloClient,
    gql,
} from '@apollo/client';
import cn from 'classnames';
import queryString from 'query-string';
import { analyticService } from 'global/services';

import { getTrialScenarioFromUrl } from 'app/utils/trial-scenario';
import { subscriptionTypeVar, selectedPeriodVar, checkoutDeliveryDateVar } from 'app/apollo/reaction';
import BASKET_QUERY from 'app/graphql/network/basket/basketQuery';
import DELVIERY_BASKET_QUERY from 'app/graphql/network/basket/deliveryBasketQuery';
import { ADD_ITEM_TO_CART } from 'app/graphql/network/basket/basketMutations';

import { CATEGORY_MAIN } from 'app/const/categories';
import { CROSSED_PRICE_DISCOUNT_TYPE } from 'app/const/crossedPricesPromo';
import { useCartMutationInBasket } from 'app/views/Basket/hooks';
import {
    removeAddedDish,
    saveDishAddedPeriod,
} from 'app/views/Basket/components/BasketNewUpsell/components/BasketTopDishCard/addedPeriodStorage';
import { setAnoterPeriodHandler } from 'app/containers/contexts/filters.context/filters.context';
import { DISH_FRAGMENT, CART_ITEM_FRAGMENT } from './portions-buttons-queries.graphql';

import cross from './img/cross.img.svg';
import plus from './img/plus.spa.img.svg';
import minus from './img/minus.img.svg';

import s from './portions-button.scss';

const BASKET_FOR_DETAILS_QUERY = gql`
    query BASKET_FOR_DETAILS_QUERY(
    $period: String!
) {
    cart(
        period: $period
    ) {
        period
        discount_conditions {
            isPromoCodeActivated
            discountType
        }
    }
}`;


export function PortionsButtonGroup(props) {
    const {
        id, selectedDishId, selectedDishPortions, refetchCallback = () => { }, period: periodProp,
    } = props;

    const client = useApolloClient();
    const subscriptionType = useReactiveVar(subscriptionTypeVar);
    const selectedPeriod = useReactiveVar(selectedPeriodVar);
    const checkoutDeliveryDate = useReactiveVar(checkoutDeliveryDateVar);
    const isEasterBoxPage = window.location?.pathname?.includes('easter');

    /* @note periodProp приходит, когда деталку открыли для блюда из чужого
     * цикла (макси-блок): добавляем и читаем корзину найденного периода,
     * а не выбранного пользователем */
    const cartPeriod = periodProp || selectedPeriod;
    const isFurtherPeriod = Boolean(periodProp) && periodProp !== selectedPeriod;

    /* STATE */

    const [isBasketUpdating, setIsBasketUpdating] = useState(false);

    const deliveryId = useMemo(
        () => window.location.pathname.split('/').find((e) => Boolean(e) && !Number.isNaN(Number(e))),
        [],
    );

    const { mutateItemInCart } = useCartMutationInBasket({ delivery_Id: deliveryId });

    const isDetailDishPage = useMemo(
        () => window.location.pathname.includes('dish'),
        [],
    );

    const isDeliveryMenu = useMemo(
        () => window.location.pathname.includes('app/delivery'),
        [],
    );

    const { data: currentBasketData } = useQuery(
        isDeliveryMenu
            ? DELVIERY_BASKET_QUERY
            : BASKET_QUERY,
        {
            variables: {
                period: selectedPeriod,
                delivery_date: checkoutDeliveryDate,
                trial_scenario: getTrialScenarioFromUrl(),
                deliveryId: (!isDetailDishPage && deliveryId) ? deliveryId : undefined,
            },
            context: {
                message: 'app:query:PortionsButtonGroup:current',
            },
        },
    );

    const hasItemsInCurrentBasket = useMemo(() => {
        if (!currentBasketData?.cart?.sections) return false;
        const flatSection = currentBasketData.cart.sections.reduce((acc, section) => [...acc, ...section.items], []);
        return flatSection.some((item) => item.portions > 0);
    }, [currentBasketData]);

    /* DA */

    const { data: dish } = useFragment({
        fragment: DISH_FRAGMENT,
        from: {
            __ref: `menuDish:${id}`,
        },
    });

    const { data, refetch } = useQuery(
        isDeliveryMenu
            ? DELVIERY_BASKET_QUERY
            : BASKET_QUERY,
        {
            variables: {
                period: cartPeriod,
                delivery_date: checkoutDeliveryDate,
                trial_scenario: getTrialScenarioFromUrl(),
                deliveryId: (!isDetailDishPage && deliveryId) ? deliveryId : undefined,
            },
            context: {
                message: 'app:query:PortionsButtonGroup',
            },
        },
    );

    const selectedDishAddedPortions = useMemo(
        () => {
            const flatSection = data?.cart?.sections
                .reduce((acc, section) => [...acc, ...section.items], [])
                .map((cartItem) => ({
                    id: cartItem.dish_id,
                    portions: cartItem.portions,
                }));
            return flatSection?.find((d) => d.id === selectedDishId)?.portions || 0;
        },
        [selectedDishId, data],
    );

    /* SELECTORS */

    const isRecurrent = useMemo(
        () => {
            const searchParams = queryString.parse(window.location.search);
            return searchParams?.payment && searchParams?.payment === 'recurrent';
        },
        [],
    );

    const isDisabled = useMemo(
        () => isBasketUpdating || dish?.visibleStatus === 2,
        [isBasketUpdating, dish?.visibleStatus],
    );

    const cartItem = useMemo(() => {
        const defaultCartItem = {
            id,
            portions: 0,
            price: 0,
            discount_price: 0,
        };

        if (!data) return defaultCartItem;
        const flatSection = data.cart.sections.reduce((acc, section) => [...acc, ...section.items], []);

        const nextCartItem = flatSection.find((cartDish) => cartDish.dish_id === String(id));

        if (!nextCartItem) return defaultCartItem;

        return nextCartItem;
    }, [id, data]);

    const discountData = useQuery(
        BASKET_FOR_DETAILS_QUERY,
        {
            variables: {
                period: cartPeriod,
            },
        },
    );

    const discountConditions = discountData?.data?.cart?.discount_conditions;
    const isPromoCodeActivated = discountConditions?.isPromoCodeActivated;
    const discountType = discountConditions?.discountType;

    const queryParams = queryString.parse(window.location.search);
    const selectionCategoryId = queryParams?.st;
    const isDishWithCrossedPrice = isPromoCodeActivated
        && discountType === CROSSED_PRICE_DISCOUNT_TYPE
        && (dish?.category_id === Number(CATEGORY_MAIN) || selectionCategoryId === CATEGORY_MAIN);

    const price = useMemo(() => {
        if (isDishWithCrossedPrice) {
            return dish?.promoPrice;
        }
        if (cartItem.portions > 0) {
            return (subscriptionType === 'subscription' || isRecurrent)
                ? Math.ceil(cartItem?.dish?.price * 0.9)
                : cartItem?.dish?.price;
        }
        return subscriptionType === 'subscription' && !isEasterBoxPage ? Math.ceil(dish.price * 0.9) : dish.price;
    }, [dish, cartItem, subscriptionType, isEasterBoxPage, isDishWithCrossedPrice, isRecurrent]);

    const [updateDishCount] = useMutation(ADD_ITEM_TO_CART, {
        variables: {
            dish_id: id,
            period: cartPeriod,
            delivery_id: (!isDetailDishPage && deliveryId) ? deliveryId : undefined,
        },
        onCompleted: async () => {
            await refetch();
            await refetchCallback();
            setIsBasketUpdating(false);
        },
    });

    const isDishInCart = cartItem.portions > 0;

    /* HANDLERS */

    const handlePlus = useCallback(() => {
        const step = dish.portions.at(0);
        const max = dish.portions.at(-1);
        const nextPortions = Math.min(cartItem.portions + step, max);

        setIsBasketUpdating(true);

        const rootAndUpsellDishesPair = localStorage.getItem(`upsellDishes:${id}:${selectedDishId}`);

        if (selectedDishId && !rootAndUpsellDishesPair) {
            const selectedDishPortionsIndex = selectedDishPortions
                .findIndex((p) => p === selectedDishAddedPortions) + 1;
            mutateItemInCart(
                {
                    dishes: [
                        { dish_id: id, portions: nextPortions },
                        { dish_id: selectedDishId, portions: selectedDishPortions[selectedDishPortionsIndex] },
                    ],
                    onCompletedCallback: async () => {
                        await refetch();
                        await refetchCallback();
                        setIsBasketUpdating(false);
                        localStorage.setItem(`upsellDishes:${id}:${selectedDishId}`, 'added');
                    },
                },
            );
        } else {
            updateDishCount({ variables: { portions: nextPortions } })
                .then(() => {
                    if (isFurtherPeriod && nextPortions > 0) {
                        if (hasItemsInCurrentBasket) {
                            saveDishAddedPeriod(id, cartPeriod);
                        } else {
                            setAnoterPeriodHandler(cartPeriod);
                        }
                    }
                })
                .catch(() => { });
        }

        if (!isDishInCart) {
            const parsedSearchParams = queryString.parse(window.location.search);
            const isDetailPopupFromSelection = Boolean(parsedSearchParams?.st);
            const location = window.location.pathname.replace(/^\s*\/|\/\s*$/g, '');
            const source = isDetailPopupFromSelection && location === 'basket'
                ? 'upsell'
                : location;

            analyticService.push({
                eventName: 'add_to_cart',
                dishes: [{ ...dish, categoryId: dish.category_id, portion: nextPortions }],
                source,
                subscriptionType,
            });
        }
    }, [
        updateDishCount, refetch, refetchCallback, mutateItemInCart,
        cartItem.portions, dish, selectedDishId, selectedDishPortions, selectedDishAddedPortions,
        isDishInCart, id, subscriptionType, isFurtherPeriod, cartPeriod, hasItemsInCurrentBasket,
    ]);

    const handleMinus = useCallback(() => {
        const step = dish.portions.at(0);
        const min = 0;
        const nextPortions = Math.max(cartItem.portions - step, min);

        setIsBasketUpdating(true);

        if (selectedDishId && nextPortions === 0) {
            const selectedDishPortionsIndex = selectedDishPortions
                .findIndex((portion) => portion === selectedDishAddedPortions);
            mutateItemInCart(
                {
                    dishes: [
                        { dish_id: id, portions: nextPortions },
                        { dish_id: selectedDishId, portions: selectedDishPortions[selectedDishPortionsIndex - 1] },
                    ],
                    onCompletedCallback: async () => {
                        await refetch();
                        await refetchCallback();
                        setIsBasketUpdating(false);
                        localStorage.removeItem(`upsellDishes:${id}:${selectedDishId}`);
                    },
                },
            );
        } else {
            /* @note убрали блюдо чужого цикла — снимаем запись, иначе чекаут
             * попробует создать пустой доп. инвойс */
            updateDishCount({ variables: { portions: nextPortions } })
                .then(() => {
                    if (isFurtherPeriod && nextPortions === 0) {
                        removeAddedDish(id);
                    }
                })
                .catch(() => { });
        }
    }, [
        updateDishCount, mutateItemInCart, refetch, refetchCallback,
        dish.portions, id, selectedDishId, cartItem.portions, isFurtherPeriod, cartPeriod,
    ]);


    /* STYLE */
    const contentClasses = cn({
        [s['portions-content']]: true,
        [s['in-cart']]: isDishInCart,
        [s['is-updating']]: isBasketUpdating,
    });

    const priceClasses = cn({
        [s['portions-button-group__price']]: true,
        [s.disabled]: dish?.visibleStatus === 2,
    });

    const buttonGroupClasses = cn({
        [s['portions-button-group']]: true,
        [s.disabled]: dish?.visibleStatus === 2,
    });

    const countClasses = cn({
        [s['portions-button-group__count']]: true,
        [s.disabled]: dish?.visibleStatus === 2,
    });

    return (
        <div className={s['portions-button-group-container']}>
            <div className={buttonGroupClasses}>
                <div className={contentClasses}>
                    {isDishInCart && !isDisabled && (
                        <div className={s['portions-button-group__button-icon-container']}>
                            <img className={s['portions-button-group__button-icon']} src={minus} alt="минус" />
                        </div>
                    )}
                    <div className={s['portions-button-group__button-price-container']}>
                        {isDishInCart && (
                            <div className={s['portions-button-group__button-count-price']}>
                                <span className={countClasses}>{cartItem.portions}</span>
                                <img className={s['portions-button-group__cross']} src={cross} alt="умножить" />
                            </div>
                        )}
                        <div className={priceClasses}>
                            {price}
                            &nbsp;
                            <span className={s['portions-button-group__price-rub']}>₽</span>
                        </div>
                    </div>
                    {!isDisabled && (
                        <div className={s['portions-button-group__button-icon-container']}>
                            <img className={s['portions-button-group__button-icon']} src={plus} alt="плюс" />
                        </div>
                    )}
                </div>

                <div className={s['portions-button-group__controls']}>
                    {isDishInCart && (
                        <button
                            className={s['portions-button-group__control']}
                            type="button"
                            onClick={handleMinus}
                            disabled={isDisabled}
                        >
                            -
                        </button>
                    )}
                    <button
                        className={s['portions-button-group__control']}
                        type="button"
                        onClick={handlePlus}
                        disabled={isDisabled}
                    >
                        +
                    </button>
                </div>
            </div>
        </div>
    );
}

PortionsButtonGroup.propTypes = {
    id: PropTypes.string.isRequired,
    refetchCallback: PropTypes.func,
    period: PropTypes.string,
};
